/**
 * Video Player Component
 * Handles HLS video playback with custom controls
 */

// Check if device is mobile
function isMobile() {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

class VideoPlayer {
    constructor() {
        this.video = document.getElementById('video-player');

        // iOS: ensure inline playback (not fullscreen by default)
        if (this.video) {
            this.video.setAttribute('playsinline', '');
            this.video.setAttribute('webkit-playsinline', '');
        }

        this.container = document.querySelector('.video-container');
        this.overlay = document.getElementById('player-overlay');
        this.nowPlaying = document.getElementById('now-playing');
        this.hls = null;
        this.currentChannel = null;
        // Incremented at the top of every play() call and captured locally as
        // myGeneration; checked after each await inside play() so a slower,
        // superseded call (e.g. channel A selected, then B selected before A's
        // probe/session-start round trip finishes) can detect it's stale and
        // bail instead of hijacking playback back to A and/or leaking B's
        // now-orphaned transcode session server-side.
        this.playGeneration = 0;
        this.overlayTimer = null;
        this.overlayDuration = 5000; // 5 seconds
        this.isUsingProxy = false;
        this.currentUrl = null;
        this.settingsLoaded = false;

        // Settings - start with defaults, load from server async
        this.settings = this.getDefaultSettings();

        // Load settings from server, then init
        this.loadSettingsFromServer().then(() => {
            this.init();
        });
    }

    /**
     * Default settings
     */
    getDefaultSettings() {
        return {
            arrowKeysChangeChannel: true,
            overlayDuration: 5,
            defaultVolume: 80,
            rememberVolume: true,
            lastVolume: 80,
            autoPlayNextEpisode: false,
            forceProxy: false,
            forceTranscode: false,
            forceRemux: false,
            autoTranscode: true,
            streamFormat: 'm3u8',
            epgRefreshInterval: '24'
        };
    }

    /**
     * Load settings from server API
     */
    async loadSettingsFromServer() {
        try {
            const serverSettings = await API.settings.get();
            this.settings = { ...this.getDefaultSettings(), ...serverSettings };
            this.settingsLoaded = true;
            console.log('[Player] Settings loaded from server');
        } catch (err) {
            console.warn('[Player] Failed to load settings from server, using defaults:', err.message);
            // Fall back to localStorage for backwards compatibility
            try {
                const saved = localStorage.getItem('streamcast_player_settings');
                if (saved) {
                    this.settings = { ...this.getDefaultSettings(), ...JSON.parse(saved) };
                    console.log('[Player] Settings loaded from localStorage (fallback)');
                }
            } catch (localErr) {
                console.error('[Player] Error loading localStorage settings:', localErr);
            }
        }
    }

    /**
     * Save settings to server API
     */
    async saveSettings() {
        try {
            await API.settings.update(this.settings);
            console.log('[Player] Settings saved to server');
        } catch (err) {
            console.error('[Player] Error saving settings to server:', err);
            // Also save to localStorage as backup
            try {
                localStorage.setItem('streamcast_player_settings', JSON.stringify(this.settings));
            } catch (localErr) {
                console.error('[Player] Error saving to localStorage:', localErr);
            }
        }
    }

    /**
     * Legacy sync method for compatibility - calls async version
     */
    loadSettings() {
        return this.settings;
    }

    /**
     * Handle a native <video> 'error' event - the safety net for playback
     * failures hls.js never sees (transcode-session fallback, direct-play,
     * raw M3U URLs).
     */
    handleVideoError() {
        const error = this.video.error;
        if (!error) return;

        // ABORTED fires on ordinary channel-switching (a new load
        // interrupting this one) - not a real failure.
        if (error.code === MediaError.MEDIA_ERR_ABORTED) return;

        console.error('[Player] Video element error:', error.code, error.message);

        const messages = {
            [MediaError.MEDIA_ERR_NETWORK]: 'Network error - lost connection while loading the stream.',
            [MediaError.MEDIA_ERR_DECODE]: 'This stream\'s format couldn\'t be decoded by your browser.',
            [MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED]: 'Stream unavailable - the provider didn\'t return a playable stream. Try again or pick another channel.'
        };
        this.showError(messages[error.code] || 'Playback error - please try selecting the channel again.');
        this.loadingSpinner?.classList.remove('show');
    }

    /**
     * Play an HLS URL via hls.js. Buffers as aggressively as possible - see
     * the matching comment in WatchPage.js's playHls() for the full
     * reasoning (maxBufferLength/maxMaxBufferLength uncapped, maxBufferSize
     * kept at a generous but finite 2GB to protect the browser tab's own
     * memory, which is the real constraint here, not the server). For a
     * transcoded live channel specifically, this is naturally bounded
     * anyway by the server's own sliding window (~6 min of segments kept on
     * disk per live session - see LIVE_WINDOW_SEGMENTS in
     * transcodeSession.js), so hls.js can never buffer more than that
     * regardless of this config. A fresh instance is created per load, per
     * hls.js's own usage pattern.
     */
    playHls(url) {
        if (this.hls) {
            this.hls.destroy();
        }
        this.hls = new Hls({
            maxBufferLength: Infinity,
            maxMaxBufferLength: Infinity,
            maxBufferSize: 2 * 1024 * 1024 * 1024, // 2GB
            backBufferLength: Infinity,
            // See the matching comment in WatchPage.js's playHls() - lets
            // hls.js jump over a small segment-boundary gap internally
            // instead of visibly stalling.
            maxBufferHole: 2
        });
        this.hls.loadSource(url);
        this.hls.attachMedia(this.video);
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => this.attemptHlsPlay());
        this.hls.on(Hls.Events.ERROR, (event, data) => this.onHlsError(data));
        // Paired with the playback_stalled report in onHlsError() below - see
        // the matching comment in WatchPage.js's playHls().
        this.hls.on(Hls.Events.FRAG_LOADED, () => {
            if (this._stallStartTime) {
                API.telemetry.report('playback_stall_recovered', {
                    channelId: this.currentChannel?.id,
                    stalledForMs: Date.now() - this._stallStartTime
                });
                this._stallStartTime = null;
            }
        });
    }

    attemptHlsPlay() {
        API.telemetry.report('playback_started', {
            channelId: this.currentChannel?.id,
            name: this.currentChannel?.name || this.currentChannel?.tvgName
        });
        this.video.play().catch(e => {
            if (e.name !== 'AbortError') console.log('Autoplay prevented:', e);
        });
    }

    /**
     * hls.js fatal-error handling, following hls.js's own documented
     * pattern: a fatal network error retries via our proxy endpoint (the
     * one thing hls.js can't know about on its own), a fatal media error
     * calls recoverMediaError(), anything else destroys the instance.
     */
    onHlsError(data) {
        // A real playback stall (the buffer stopped growing) is reported by
        // hls.js as a non-fatal MEDIA_ERROR, not a fatal one - so it looks
        // like "stuck loading forever despite having buffered data" from the
        // outside: readyState/buffered still show whatever was already
        // downloaded, but the fragment LOADER itself has stopped fetching
        // more. recoverMediaError() below (for the fatal-media-error case)
        // only resets the MSE buffer and wouldn't help here; startLoad() is
        // what actually resumes fragment loading. Must be handled before the
        // `if (!data.fatal) return;` below, which would otherwise silently
        // swallow this exact event.
        if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            console.warn('[Hls] Buffer stalled, restarting fragment loading...');
            if (!this._stallStartTime) {
                this._stallStartTime = Date.now();
                API.telemetry.report('playback_stalled', {
                    channelId: this.currentChannel?.id,
                    name: this.currentChannel?.name || this.currentChannel?.tvgName,
                    positionSec: this.video?.currentTime
                });
            }
            this.hls?.startLoad();
            return;
        }

        if (!data.fatal) return;

        console.error('[Hls] Fatal error:', data.type, data.details);

        switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                if (!this.isUsingProxy && this.currentUrl && !this.currentUrl.startsWith('/api/')) {
                    console.log('[Hls] Fatal network error, retrying via proxy...');
                    this.isUsingProxy = true;
                    this.playHls(this.getProxiedUrl(this.currentUrl));
                } else {
                    console.error('[Hls] Unrecoverable network error');
                    this.showError('Playback error - please try selecting the channel again.');
                }
                break;
            case Hls.ErrorTypes.MEDIA_ERROR:
                this.hls.recoverMediaError();
                break;
            default:
                this.hls.destroy();
                this.hls = null;
                this.showError('Playback error - please try selecting the channel again.');
                break;
        }
    }

    /**
     * Initialize custom video controls for mobile
     */
    /**
     * Initialize custom video controls
     */
    initCustomControls() {
        // Elements
        this.controlsOverlay = document.getElementById('player-controls-overlay');
        this.loadingSpinner = document.getElementById('player-loading');

        // iOS Safari: detect and compensate for floating bottom toolbar
        const updateIosUiBottom = () => {
            let uiBottom = 0;
            if (window.visualViewport) {
                const vv = window.visualViewport;
                uiBottom = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
            }
            document.documentElement.style.setProperty('--ios-ui-bottom', uiBottom + 'px');
        };

        updateIosUiBottom();

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateIosUiBottom);
            window.visualViewport.addEventListener('scroll', updateIosUiBottom);
        } else {
            window.addEventListener('resize', updateIosUiBottom);
        }

        // iOS: use custom --vh unit to avoid 100vh issues with dynamic toolbar
        const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent);
        if (isIOS && this.container) {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
            this.container.style.height = 'calc(var(--vh) * 100)';
        }

        // Apply safe area + iOS toolbar padding to controls overlay
        if (this.controlsOverlay) {
            this.controlsOverlay.style.paddingBottom = 'calc(env(safe-area-inset-bottom, 0px) + var(--ios-ui-bottom, 0px) + 12px)';
        }

        const btnPlay = document.getElementById('btn-play');
        const btnMute = document.getElementById('btn-mute');
        const btnFullscreen = document.getElementById('btn-fullscreen');
        const volumeSlider = document.getElementById('player-volume');
        const channelNameEl = document.getElementById('player-channel-name');

        if (!this.controlsOverlay) return;

        // Disable native controls
        this.video.controls = false;

        // Initial State: Hide all overlay elements until content is loaded
        this.loadingSpinner?.classList.remove('show');
        this.controlsOverlay?.classList.add('hidden');

        // Play/Pause toggle
        const togglePlay = () => {
            if (this.video.paused) {
                this.video.play();
            } else {
                this.video.pause();
            }
        };

        btnPlay?.addEventListener('click', (e) => {
            e.stopPropagation();
            togglePlay();
        });

        // Center play button (large button shown when paused)
        const centerPlayBtn = document.getElementById('player-center-play');
        centerPlayBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            togglePlay();
        });

        // Tap-to-toggle on the raw video element is handled by the shared
        // container click listener below (not here too) - see its comment
        // for why a separate handler here used to make every mobile tap
        // instantly pause/resume instead of first revealing the controls.

        // Update play/pause UI
        const updatePlayUI = () => {
            const isPaused = this.video.paused;
            const hasVideo = this.video.src && this.video.src !== '' && this.video.readyState > 0;

            // Bottom bar button
            const iconPlay = btnPlay?.querySelector('.icon-play');
            const iconPause = btnPlay?.querySelector('.icon-pause');

            if (iconPlay && iconPause) {
                iconPlay.classList.toggle('hidden', !isPaused);
                iconPause.classList.toggle('hidden', isPaused);
            }

            // Center play button - show only when paused AND video is loaded
            if (centerPlayBtn) {
                centerPlayBtn.classList.toggle('show', isPaused && hasVideo);
            }
        };

        this.video.addEventListener('play', updatePlayUI);
        this.video.addEventListener('pause', updatePlayUI);

        // Loading spinner
        this.video.addEventListener('waiting', () => {
            this.loadingSpinner?.classList.add('show');
        });

        this.video.addEventListener('canplay', () => {
            this.loadingSpinner?.classList.remove('show');
        });

        // Auto-clear a stale error overlay once playback is DEFINITELY still
        // progressing. Reported bug: a transient hls.js/native error (which
        // hls.js's own internal retry, or the proxy-retry in onHlsError(),
        // then recovered from on its own) left the error message overlay
        // stuck on screen forever - audio kept playing, but the overlay sits
        // on top of the video element, visually blocking it, and nothing
        // previously cleared it since showError() is only ever undone by
        // starting a NEW play() call (selecting a channel again). timeupdate
        // only fires while currentTime is actively advancing (never during a
        // real stall/buffering wait), so it's a reliable "this is genuinely
        // still playing" signal regardless of which code path silently
        // fixed the underlying error, or whether a 'playing' transition
        // event even fired again (it wouldn't if the video never actually
        // stopped advancing in the first place).
        this.video.addEventListener('timeupdate', () => {
            if (this.overlay.classList.contains('has-error') && !this.video.paused) {
                this.overlay.classList.add('hidden');
                this.overlay.classList.remove('has-error');
            }
        });

        // Native <video> error - the only signal available when hls.js isn't
        // the one managing playback (e.g. the transcode-session fallback and
        // direct-play paths just do `video.src = url; video.play()`, with no
        // hls.js error handler watching). Without this, a stream that fails
        // in one of those paths left the spinner stuck forever with zero
        // feedback - onHlsError() only ever sees hls.js-managed playback.
        this.video.addEventListener('error', () => this.handleVideoError());

        // Mute/Volume
        const updateVolumeUI = () => {
            const isMuted = this.video.muted || this.video.volume === 0;
            const iconVol = btnMute?.querySelector('.icon-vol');
            const iconMuted = btnMute?.querySelector('.icon-muted');

            if (iconVol && iconMuted) {
                iconVol.classList.toggle('hidden', isMuted);
                iconMuted.classList.toggle('hidden', !isMuted);
            }

            if (volumeSlider) {
                volumeSlider.value = this.video.muted ? 0 : Math.round(this.video.volume * 100);
            }
        };

        btnMute?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.video.muted) {
                this.video.muted = false;
                this.video.volume = (parseInt(volumeSlider?.value || 80) / 100) || 0.8;
            } else {
                this.video.muted = true;
            }
            updateVolumeUI();
        });

        volumeSlider?.addEventListener('input', (e) => {
            e.stopPropagation();
            const val = parseInt(e.target.value);
            this.video.volume = val / 100;
            this.video.muted = val === 0;
            updateVolumeUI();
        });

        this.video.addEventListener('volumechange', updateVolumeUI);

        // Captions
        this.captionsBtn = document.getElementById('player-captions-btn');
        this.captionsMenu = document.getElementById('player-captions-menu');
        this.captionsList = document.getElementById('player-captions-list');
        this.captionsMenuOpen = false;

        this.captionsBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleCaptionsMenu();
        });

        // Close captions menu when clicking outside
        document.addEventListener('click', (e) => {
            if (this.captionsMenuOpen &&
                !this.captionsMenu.contains(e.target) &&
                !this.captionsBtn.contains(e.target)) {
                this.closeCaptionsMenu();
            }
        });

        // Fullscreen
        btnFullscreen?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleFullscreen();
        });

        // Picture-in-Picture
        const btnPip = document.getElementById('btn-pip');
        btnPip?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePictureInPicture();
        });

        // Chromecast + AirPlay
        this.initCastControls();

        this.container.addEventListener('dblclick', () => this.toggleFullscreen());

        // Overlay Auto-hide Logic
        let overlayTimeout;
        const sidebarExpandBtn = document.getElementById('sidebar-expand-btn');

        const showOverlay = () => {
            this.controlsOverlay.classList.remove('hidden');
            this.container.style.cursor = 'default';
            sidebarExpandBtn?.classList.add('visible');
            resetOverlayTimer();
        };

        const hideOverlay = () => {
            if (!this.video.paused) {
                this.controlsOverlay.classList.add('hidden');
                this.container.style.cursor = 'none';
                sidebarExpandBtn?.classList.remove('visible');
            }
        };

        const resetOverlayTimer = () => {
            clearTimeout(overlayTimeout);
            if (!this.video.paused) {
                overlayTimeout = setTimeout(hideOverlay, 3000);
            }
        };

        // Click/tap behavior, split by input type - matches each platform's
        // own market-standard player (researched both explicitly, see
        // WatchPage.js's matching block for the full reasoning):
        // - Desktop (mouse): hover reveals controls; a click ALWAYS toggles
        //   play/pause immediately, regardless of current controls
        //   visibility (YouTube/Netflix behavior) - must not depend on
        //   whether a mousemove happened to fire first, since the cursor
        //   may already be sitting motionless over the video.
        // - Touch (mobile): first tap only reveals the controls; only a tap
        //   while they're already visible toggles playback.
        // touchstart always fires before click for the same physical tap,
        // so its presence signals "this gesture is touch, use mobile
        // semantics", and it captures the pre-gesture visibility (the live
        // value would already be flipped true by showOverlay() below).
        let touchStartOverlayVisible = null;
        this.container.addEventListener('mousemove', showOverlay);
        this.container.addEventListener('touchstart', () => {
            touchStartOverlayVisible = !this.controlsOverlay?.classList.contains('hidden');
            showOverlay();
        }, { passive: true });
        this.container.addEventListener('click', (e) => {
            // Only handle taps/clicks on the video/container background (not controls)
            if (e.target !== this.video && e.target !== this.container && !e.target.classList.contains('watch-overlay')) return;

            if (touchStartOverlayVisible !== null) {
                const wasVisible = touchStartOverlayVisible;
                touchStartOverlayVisible = null;
                if (wasVisible) {
                    togglePlay();
                } else {
                    showOverlay();
                }
            } else {
                togglePlay();
                showOverlay();
            }
        });

        this.video.addEventListener('play', resetOverlayTimer);
        this.video.addEventListener('pause', showOverlay);

        // Update Title when channel changes
        window.addEventListener('channelChanged', (e) => {
            if (channelNameEl && e.detail) {
                channelNameEl.textContent = e.detail.name || e.detail.tvgName || 'Live TV';
            }
            showOverlay();
        });

        // Initial state
        updatePlayUI();
        updateVolumeUI();
    }

    /**
     * Toggle Picture-in-Picture mode (cross-browser including Safari)
     */
    async togglePictureInPicture() {
        try {
            // Standard PiP API (Chrome, Edge, Firefox)
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
            } else if (document.pictureInPictureEnabled && this.video.readyState >= 2) {
                await this.video.requestPictureInPicture();
            }
            // Safari fallback using webkitPresentationMode
            else if (typeof this.video.webkitSetPresentationMode === 'function') {
                const mode = this.video.webkitPresentationMode;
                this.video.webkitSetPresentationMode(mode === 'picture-in-picture' ? 'inline' : 'picture-in-picture');
            }
        } catch (err) {
            if (err.name !== 'NotAllowedError') {
                console.error('Picture-in-Picture error:', err);
            }
        }
    }

    /**
     * Wire up Chromecast + AirPlay buttons. Both are feature-detected and
     * hidden (see the `hidden` class in index.html) until confirmed
     * available - Cast only via the async 'cast-api-available' event (SDK
     * loads and discovers devices asynchronously), AirPlay synchronously
     * since it's just a native <video> method check.
     */
    initCastControls() {
        const btnCast = document.getElementById('btn-cast');
        const btnAirplay = document.getElementById('btn-airplay');

        if (btnCast) {
            const showIfAvailable = () => btnCast.classList.toggle('hidden', !window.Cast?.isAvailable());
            if (window.Cast?.isAvailable()) showIfAvailable();
            window.addEventListener('cast-api-available', showIfAvailable);

            btnCast.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleCasting();
            });

            window.addEventListener('cast-state-changed', (e) => {
                btnCast.classList.toggle('active', e.detail.isCasting);
                // Avoid the local tab and the TV both streaming/decoding at
                // once - pause locally once the TV takes over playback.
                if (e.detail.isCasting) this.video.pause();
            });
        }

        // AirPlay (Safari/WebKit only) - webkitShowPlaybackTargetPicker()
        // opens Safari's own native device picker and handles the entire
        // remote-playback session itself; we only need to trigger it and
        // reflect its active state, not manage the session.
        if (btnAirplay && typeof this.video.webkitShowPlaybackTargetPicker === 'function') {
            btnAirplay.classList.remove('hidden');
            btnAirplay.addEventListener('click', (e) => {
                e.stopPropagation();
                this.video.webkitShowPlaybackTargetPicker();
            });
            this.video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', () => {
                btnAirplay.classList.toggle('active', this.video.webkitCurrentPlaybackTargetIsWireless);
            });
        }
    }

    /**
     * Start or stop casting the currently playing live channel.
     */
    toggleCasting() {
        if (!window.Cast?.isAvailable()) return;

        if (window.Cast.isCasting()) {
            window.Cast.stopCasting();
            return;
        }

        if (!this.currentUrl) {
            console.warn('[Player] No stream to cast');
            return;
        }

        // this.currentUrl may be a direct (unproxied) provider URL - its
        // credentials are already redacted server-side (see streamTokens.js),
        // which means the real provider can no longer serve it directly.
        // Route through our own proxy whenever it isn't already one of our
        // own relative URLs (transcode session/remux/proxy), so the
        // Chromecast receiver always fetches something that actually
        // resolves (our server swaps the redacted token back server-side).
        // Same "already ours?" check used by the HLS network-error retry above.
        const castUrl = this.currentUrl.startsWith('/api/') ? this.currentUrl : this.getProxiedUrl(this.currentUrl);
        const title = this.currentChannel?.name || this.currentChannel?.tvgName || 'Live TV';
        window.Cast.startCasting(window.Cast.toAbsoluteUrl(castUrl), {
            title,
            subtitle: 'StreamCast - Live',
            isLive: true
        }).catch(err => console.error('[Cast] Failed to start casting:', err));
    }


    /**
     * Toggle captions menu visibility
     */
    toggleCaptionsMenu() {
        if (!this.captionsMenu) return;

        this.captionsMenuOpen = !this.captionsMenuOpen;

        if (this.captionsMenuOpen) {
            this.updateCaptionsTracks();
            this.captionsMenu.classList.remove('hidden');
        } else {
            this.captionsMenu.classList.add('hidden');
        }
    }

    /**
     * Close captions menu
     */
    closeCaptionsMenu() {
        if (!this.captionsMenu) return;
        this.captionsMenuOpen = false;
        this.captionsMenu.classList.add('hidden');
    }

    /**
     * Update available caption tracks in the menu
     */
    updateCaptionsTracks() {
        if (!this.captionsList) return;

        // Clear existing list (keep only Off option)
        this.captionsList.innerHTML = '<button class="captions-option" data-index="-1">Off</button>';

        // Add tracks
        if (this.video.textTracks && this.video.textTracks.length > 0) {
            let hasActiveTrack = false;

            for (let i = 0; i < this.video.textTracks.length; i++) {
                const track = this.video.textTracks[i];
                const btn = document.createElement('button');
                btn.className = 'captions-option';
                btn.textContent = track.label || `Track ${i + 1} (${track.language || 'unknown'})`;
                btn.dataset.index = i;

                if (track.mode === 'showing') {
                    btn.classList.add('active');
                    // Add checkmark
                    btn.innerHTML += ' <span style="float: right;">✓</span>';
                    hasActiveTrack = true;
                }

                btn.onclick = (e) => {
                    e.stopPropagation();
                    this.selectCaptionTrack(i);
                };

                this.captionsList.appendChild(btn);
            }

            // Handle "Off" button state
            const offBtn = this.captionsList.querySelector('[data-index="-1"]');
            if (offBtn) {
                if (!hasActiveTrack) {
                    offBtn.classList.add('active');
                    offBtn.innerHTML += ' <span style="float: right;">✓</span>';
                }
                offBtn.onclick = (e) => {
                    e.stopPropagation();
                    this.selectCaptionTrack(-1);
                };
            }
        }
    }

    /**
     * Select a caption track
     */
    selectCaptionTrack(index) {
        if (!this.video.textTracks) return;

        // Turn off all tracks
        for (let i = 0; i < this.video.textTracks.length; i++) {
            this.video.textTracks[i].mode = 'hidden'; // or 'disabled'
        }

        // Turn on selected track
        if (index >= 0 && index < this.video.textTracks.length) {
            this.video.textTracks[index].mode = 'showing';
        }

        this.closeCaptionsMenu();
    }

    init() {
        // Apply default/remembered volume
        const volume = this.settings.rememberVolume ? this.settings.lastVolume : this.settings.defaultVolume;
        this.video.volume = volume / 100;

        // Save volume changes
        this.video.addEventListener('volumechange', () => {
            if (this.settings.rememberVolume) {
                this.settings.lastVolume = Math.round(this.video.volume * 100);
                this.saveSettings();
            }
        });

        // Setup custom video controls
        this.initCustomControls();

        // Detect video resolution when metadata loads (works for all streams)
        this.video.addEventListener('loadedmetadata', () => {
            if (this.video.videoHeight > 0) {
                this.currentStreamInfo = {
                    width: this.video.videoWidth,
                    height: this.video.videoHeight
                };
                this.updateQualityBadge();
            }
        });

        // The Hls instance is created lazily on first playback (see
        // playHls()) rather than up front - there's nothing to attach to
        // until a channel is actually selected.

        // Keyboard controls
        document.addEventListener('keydown', (e) => this.handleKeyboard(e));

        // Click on video shows overlay
        this.video.addEventListener('click', () => this.showNowPlayingOverlay());
    }

    /**
     * Show the now playing overlay briefly
     */
    showNowPlayingOverlay() {
        if (!this.currentChannel) return;

        // Clear existing timer
        if (this.overlayTimer) {
            clearTimeout(this.overlayTimer);
        }

        // Show overlay
        this.nowPlaying.classList.remove('hidden');

        // Hide after duration
        this.overlayTimer = setTimeout(() => {
            this.nowPlaying.classList.add('hidden');
        }, this.settings.overlayDuration * 1000);
    }

    /**
     * Hide the now playing overlay
     */
    hideNowPlayingOverlay() {
        if (this.overlayTimer) {
            clearTimeout(this.overlayTimer);
        }
        this.nowPlaying.classList.add('hidden');
    }

    /**
     * Returns { url, isHls }. isHls tells the caller whether `url` is an HLS
     * .m3u8 manifest (session succeeded - play via playHls/HLS.js) or a raw
     * fragmented-MP4 stream from the legacy /api/transcode fallback (play via
     * native <video src>). These are NOT interchangeable: feeding the raw-MP4
     * fallback URL into HLS.js's loadSource() makes it fail to parse the
     * manifest silently, so nothing plays - see the matching fix in
     * WatchPage.js for the full writeup.
     */
    async startTranscodeSession(url, options = {}) {
        try {
            console.log('[Player] Starting HLS transcode session...', options);
            const res = await fetch('/api/transcode/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, ...options })
            });
            if (!res.ok) throw new Error('Failed to start session');
            const session = await res.json();
            // Deliberately NOT setting this.currentSessionId here - the caller
            // (play()) only commits it once it has confirmed this result is
            // still for the current/latest play() call (see playGeneration).
            // Setting it unconditionally here raced: a slower call's session
            // could overwrite a faster, already-active call's session id, so
            // stopTranscodeSession()/stop() would delete the wrong session and
            // the right one would never get cleaned up server-side.
            return { url: session.playlistUrl, isHls: true, sessionId: session.sessionId };
        } catch (err) {
            console.error('[Player] Session start failed, falling back to direct transcode:', err);
            return { url: `/api/transcode?url=${encodeURIComponent(url)}`, isHls: false, sessionId: null };
        }
    }

    /**
     * Best-effort server-side cleanup for a transcode session that was
     * started by a play() call which turned out to be superseded before it
     * could commit to using it (see playGeneration in play()).
     */
    discardStaleTranscodeSession(sessionId) {
        if (!sessionId) return;
        console.log('[Player] Discarding transcode session from a superseded play() call:', sessionId);
        fetch(`/api/transcode/${sessionId}`, { method: 'DELETE' }).catch(() => {});
    }

    /**
     * Play a transcode result from startTranscodeSession(), using hls.js or
     * native <video src> depending on what the session actually returned.
     */
    playTranscodeResult({ url, isHls }) {
        if (isHls) {
            this.playHls(url);
        } else {
            this.video.src = url;
            this.video.play().catch(e => {
                if (e.name !== 'AbortError') console.error('[Player] Autoplay error:', e);
            });
        }
    }

    /**
     * Stop and cleanup current transcode session
     */
    async stopTranscodeSession() {
        if (this.currentSessionId) {
            console.log('[Player] Stopping transcode session:', this.currentSessionId);
            const sessionId = this.currentSessionId;
            this.currentSessionId = null;
            // Awaited (not fire-and-forget) - see WatchPage.js's identical
            // stopTranscodeSession() for why: this account's IPTV line only
            // allows 1 concurrent connection, so a caller about to start a
            // different stream needs this old one's provider connection
            // actually released first, not just requested.
            await fetch(`/api/transcode/${sessionId}`, { method: 'DELETE' })
                .catch(err => console.error('Failed to stop session:', err));
        }
    }

    /**
     * Common "a channel just started playing" side effects - was
     * independently duplicated across every branch of play() (5-6 near-
     * identical copies), which is how two real bugs went unnoticed: the
     * Force Video Transcode/Upscaling branch was missing this entirely
     * (stale channel name/EPG overlay, no channelChanged event for anyone
     * else listening for it), and it called updateNowPlaying(channel,
     * 'Transcoding (Video)') - passing a plain string where updateNowPlaying
     * expects an EPG-data object ({current, upcoming}), which silently did
     * nothing (a string has no .current/.upcoming, so it behaved exactly
     * like passing no second argument at all).
     */
    onPlaybackStarted(channel) {
        this.updateNowPlaying(channel);
        this.showNowPlayingOverlay();
        this.fetchEpgData(channel);
        window.dispatchEvent(new CustomEvent('channelChanged', { detail: channel }));
    }

    /**
     * Play a channel
     */
    /**
     * @param {object} channel
     * @param {string} streamUrl
     * @param {object} [options]
     * @param {{title:string, start:Date|string, stop:Date|string}} [options.catchup] -
     *   when playing back a past program via catch-up/timeshift instead of a
     *   live channel. While set, fetchEpgData()'s live "now playing" lookup is
     *   suppressed and updateNowPlaying() shows this program's info instead
     *   (see both methods below) - otherwise the live guide data for the
     *   channel would immediately overwrite the catch-up program's title.
     */
    async play(channel, streamUrl, options = {}) {
        const myGeneration = ++this.playGeneration;
        this.currentChannel = channel;

        try {
            // Stop any WatchPage playback (movies/series) before starting Live
            // TV - awaited so its transcode connection is actually released
            // before the probe below runs (this account's IPTV line only
            // allows 1 concurrent connection).
            await window.app?.pages?.watch?.stop?.();
            if (myGeneration !== this.playGeneration) return; // superseded while stopping VOD playback

            // Stop current playback. Must happen before setting catchupInfo below,
            // not after - stop() unconditionally clears catchupInfo (so a later
            // live channel selection doesn't inherit a stale catch-up state), so
            // setting it first here would just get immediately wiped out again.
            await this.stop();
            if (myGeneration !== this.playGeneration) return; // superseded while stopping previous channel
            this.catchupInfo = options.catchup || null;
            this.updateTranscodeStatus('hidden');

            // Hide "select a channel" overlay and clear any error left over
            // from a previous failed channel - a fresh selection deserves a
            // fresh loading state, not yesterday's error message flashing
            // before the spinner takes over.
            this.overlay.classList.add('hidden');
            this.overlay.classList.remove('has-error');

            // Show custom controls overlay
            this.controlsOverlay?.classList.remove('hidden');
            this.loadingSpinner?.classList.add('show');

            // Determine if HLS or direct stream
            this.currentUrl = streamUrl;

            // Clear subtitle tracks (and the captions button they earned) from
            // the previous channel up front - if autoTranscode is off, the
            // probe below never runs and stale state from an earlier channel
            // must not linger.
            this.video.querySelectorAll('track').forEach(t => t.remove());
            this.captionsBtn?.classList.add('hidden');
            if (this.captionsMenuOpen) this.closeCaptionsMenu();

            // CHECK: Auto Transcode (Smart) - probe first, then decide
            if (this.settings.autoTranscode) {
                console.log('[Player] Auto Transcode enabled. Probing stream...');
                try {
                    const probeRes = await fetch(`/api/probe?url=${encodeURIComponent(streamUrl)}`);
                    const info = await probeRes.json();
                    if (myGeneration !== this.playGeneration) return; // superseded by a newer play() call while probing
                    console.log(`[Player] Probe result: video=${info.video}, audio=${info.audio}, ${info.width}x${info.height}, compatible=${info.compatible}`);

                    // probe.js only sets sourceError when ffprobe got a definitive
                    // HTTP-level rejection from the provider (see
                    // classifyDefiniteFailure there) - not on a timeout/generic
                    // failure, which still falls through to try playing anyway.
                    // Showing this immediately (rather than proceeding into a
                    // transcode session that's doomed to fail the same way ~20s
                    // later) is exactly what surfaces "this channel is down" in
                    // both the full player and the Guide's inline preview -
                    // showError() already renders in both (see its own comment).
                    if (info.sourceError) {
                        console.warn('[Player] Probe detected a definitive provider error:', info.reason);
                        this.loadingSpinner?.classList.remove('show');
                        this.showError(info.reason);
                        return;
                    }

                    // Store probe result for quality badge display
                    this.currentStreamInfo = info;
                    this.updateQualityBadge();

                    // Handle subtitles from probe result (tracks/button already
                    // cleared/hidden up front in play() before this probe ran)
                    if (info.subtitles && info.subtitles.length > 0) {
                        console.log(`[Player] Found ${info.subtitles.length} subtitle tracks`);
                        info.subtitles.forEach(sub => {
                            const track = document.createElement('track');
                            track.kind = 'subtitles';
                            track.label = sub.title;
                            track.srclang = sub.language;
                            track.src = `/api/subtitle?url=${encodeURIComponent(streamUrl)}&index=${sub.index}`;
                            this.video.appendChild(track);
                        });
                        this.captionsBtn?.classList.remove('hidden');

                        // Force update of captions menu if it's open
                        if (this.captionsMenuOpen) {
                            this.updateCaptionsTracks();
                        }
                    }

                    // NOTE: a live "data-saver" clause used to force any live
                    // stream >=1080p through a bitrate-capped 720p RE-ENCODE here
                    // (to cut buffering on weak mobile links). It was removed: on
                    // this GPU-less box the re-encode is paced ~realtime and dips
                    // below it under CPU contention, at which point the playhead
                    // catches the live edge / a just-deleted sliding-window segment
                    // and the player stalls forever ("plays a few seconds then
                    // hangs"). The original NodeCast never re-encoded compatible
                    // live channels - it just remuxed them (container copy, ~9x
                    // realtime, never falls behind). So compatible h264/aac live
                    // (NPO1 HD etc.) now falls through to the remux path below,
                    // exactly like NodeCast. Only genuinely incompatible audio or
                    // an explicit Upscale still needs a transcode session.
                    if (info.needsTranscode || this.settings.upscaleEnabled) {
                        // Incompatible audio (AC3/EAC3/DTS) or upscaling - use a
                        // transcode session.
                        console.log(`[Player] Auto: Using HLS transcode session (${this.settings.upscaleEnabled ? 'Upscaling' : 'Incompatible audio/video'})`);

                        // h264 -> copy video (audio-only transcode); upscaling ->
                        // full re-encode.
                        const videoMode = (info.video && info.video.includes('h264') && !this.settings.upscaleEnabled) ? 'copy' : 'encode';
                        const statusText = videoMode === 'copy' ? 'Transcoding (Audio)'
                            : (this.settings.upscaleEnabled ? 'Upscaling' : 'Transcoding (Video)');
                        const statusMode = this.settings.upscaleEnabled ? 'upscaling' : 'transcoding';

                        this.updateTranscodeStatus(statusMode, statusText);
                        const transcodeResult = await this.startTranscodeSession(streamUrl, {
                            videoMode,
                            videoCodec: info.video,
                            audioCodec: info.audio,
                            audioChannels: info.audioChannels,
                            // Live: use a bounded sliding-window playlist server-side
                            // (delete old segments) so a long channel watch doesn't
                            // grow the transcode cache unbounded - VOD keeps all
                            // segments for seeking, live never needs them.
                            isLive: true
                        });
                        if (myGeneration !== this.playGeneration) {
                            this.discardStaleTranscodeSession(transcodeResult.sessionId);
                            return;
                        }
                        this.currentSessionId = transcodeResult.sessionId;
                        this.currentUrl = transcodeResult.url; // Update currentUrl for HLS reload

                        this.playTranscodeResult(transcodeResult);
                        this.onPlaybackStarted(channel);
                        return;
                    } else if (info.needsRemux) {
                        // Raw .ts container - use remux
                        console.log('[Player] Auto: Using remux (.ts container)');
                        this.updateTranscodeStatus('remuxing', 'Remux (Auto)');
                        const remuxUrl = `/api/remux?url=${encodeURIComponent(streamUrl)}&audioCodec=${encodeURIComponent(info.audio || '')}`;
                        this.currentUrl = remuxUrl;
                        this.video.src = remuxUrl;
                        this.video.play().catch(e => {
                            if (e.name !== 'AbortError') console.log('[Player] Autoplay prevented:', e);
                        });
                        this.onPlaybackStarted(channel);
                        return;
                    }
                    // Compatible - fall through to normal HLS.js path
                    console.log('[Player] Auto: Using HLS.js (compatible)');
                } catch (err) {
                    console.warn('[Player] Probe failed, using normal playback:', err.message);
                    // Continue with normal playback on probe failure
                }
            }

            // CHECK: Force Video Transcode (Full) or Upscaling
            if (this.settings.forceVideoTranscode || this.settings.upscaleEnabled) {
                const statusText = this.settings.upscaleEnabled ? 'Upscaling' : 'Transcoding (Video)';
                const statusMode = this.settings.upscaleEnabled ? 'upscaling' : 'transcoding';
                console.log(`[Player] ${statusText} enabled. Starting session (encode)...`);
                this.updateTranscodeStatus(statusMode, statusText);
                const transcodeResult = await this.startTranscodeSession(streamUrl, { videoMode: 'encode', isLive: true });
                if (myGeneration !== this.playGeneration) {
                    this.discardStaleTranscodeSession(transcodeResult.sessionId);
                    return;
                }
                this.currentSessionId = transcodeResult.sessionId;
                this.currentUrl = transcodeResult.url;

                this.playTranscodeResult(transcodeResult);
                this.onPlaybackStarted(channel);
                return; // Exit
            }

            // CHECK: Force Audio Transcode (Copy Video) - legacy forceTranscode setting
            if (this.settings.forceTranscode) {
                console.log('[Player] Force Audio Transcode enabled. Starting session (copy)...');
                this.updateTranscodeStatus('transcoding', 'Transcoding (Audio)');

                // Probe to get video codec for HEVC tag handling
                let videoCodec = 'unknown';
                try {
                    const probeRes = await fetch(`/api/probe?url=${encodeURIComponent(streamUrl)}`);
                    const info = await probeRes.json();
                    if (myGeneration !== this.playGeneration) return; // superseded while probing
                    if (info.sourceError) {
                        console.warn('[Player] Probe detected a definitive provider error:', info.reason);
                        this.loadingSpinner?.classList.remove('show');
                        this.showError(info.reason);
                        return;
                    }
                    videoCodec = info.video;
                } catch (e) { console.warn('Probe failed for force audio, assuming h264'); }
                if (myGeneration !== this.playGeneration) return; // superseded while probing (catch path)

                const transcodeResult = await this.startTranscodeSession(streamUrl, { videoMode: 'copy', videoCodec, isLive: true });
                if (myGeneration !== this.playGeneration) {
                    this.discardStaleTranscodeSession(transcodeResult.sessionId);
                    return;
                }
                this.currentSessionId = transcodeResult.sessionId;
                this.currentUrl = transcodeResult.url;

                console.log('[Player] Playing transcoded stream:', transcodeResult.url, transcodeResult.isHls ? '(HLS)' : '(direct)');
                this.playTranscodeResult(transcodeResult);

                this.onPlaybackStarted(channel);
                return; // Exit early
            }

            // Proactively use proxy for:
            // 1. User enabled "Force Proxy" in settings
            // 2. Known CORS-restricted domains (like Pluto TV)
            // 3. Mixed content: an http:// stream URL on an https:// page is
            //    GUARANTEED to be blocked by the browser before a single byte
            //    is fetched - there is nothing to try. Without this check,
            //    every channel change on the HTTPS domain burned ~30-60s in
            //    hls.js's internal retries + our 3-attempt backoff before
            //    finally falling back to the proxy anyway.
            // Note: Xtream sources are NOT otherwise auto-proxied because many
            // providers IP-lock streams (server IP != viewer IP can 403).
            const proxyRequiredDomains = ['pluto.tv'];
            const isMixedContent = window.location.protocol === 'https:' && streamUrl.startsWith('http://');
            const needsProxy = this.settings.forceProxy || isMixedContent || proxyRequiredDomains.some(domain => streamUrl.includes(domain));

            this.isUsingProxy = needsProxy;
            const finalUrl = needsProxy ? this.getProxiedUrl(streamUrl) : streamUrl;

            // Detect if this is likely an HLS stream (has .m3u8 in URL)
            const looksLikeHls = finalUrl.includes('.m3u8') || finalUrl.includes('m3u8');

            // Check if this looks like a raw stream (no HLS manifest, no common video extensions)
            // This includes .ts files AND extension-less URLs that might be TS streams
            const isRawTs = finalUrl.includes('.ts') && !finalUrl.includes('.m3u8');
            const isExtensionless = !finalUrl.includes('.m3u8') &&
                !finalUrl.includes('.mp4') &&
                !finalUrl.includes('.mkv') &&
                !finalUrl.includes('.avi') &&
                !finalUrl.includes('.ts');

            // Force Remux: Route through FFmpeg for container conversion
            // Applies to: 1) .ts streams when detected, or 2) ALL non-HLS streams when enabled
            if (this.settings.forceRemux && (isRawTs || isExtensionless)) {
                console.log('[Player] Force Remux enabled. Routing through FFmpeg remux...');
                console.log('[Player] Stream type:', isRawTs ? 'Raw TS' : 'Extension-less (assumed TS)');
                this.updateTranscodeStatus('remuxing', 'Remux (Force)');
                const remuxUrl = this.getRemuxUrl(streamUrl);
                this.video.src = remuxUrl;
                this.video.play().catch(e => {
                    if (e.name !== 'AbortError') console.log('[Player] Autoplay prevented:', e);
                });

                this.onPlaybackStarted(channel);
                return;
            }

            // If raw TS detected without Force Remux enabled, show error
            if (isRawTs && !this.settings.forceRemux) {
                console.warn('[Player] Raw MPEG-TS stream detected. Browsers cannot play .ts files directly.');
                this.showError(
                    'This stream uses raw MPEG-TS format (.ts) which browsers cannot play directly.<br><br>' +
                    '<strong>To fix this:</strong><br>' +
                    '1. Enable <strong>"Force Remux"</strong> in Settings → Streaming<br>' +
                    '2. Or configure your source to output HLS (.m3u8) format'
                );
                return;
            }

            // Priority 1: Use hls.js for HLS streams on browsers that support it
            if (looksLikeHls && Hls.isSupported()) {
                this.updateTranscodeStatus('direct', 'Direct HLS');
                this.playHls(finalUrl);
            } else if (this.video.canPlayType('application/vnd.apple.mpegurl') === 'probably' ||
                this.video.canPlayType('application/vnd.apple.mpegurl') === 'maybe') {
                // Priority 2: Native HLS support (Safari on iOS/macOS where HLS.js may not work)
                this.updateTranscodeStatus('direct', 'Direct Native');
                this.video.src = finalUrl;
                this.video.play().catch(e => {
                    if (e.name === 'AbortError') return; // Ignore interruption by new load
                    console.log('Autoplay prevented, trying proxy if CORS error:', e);
                    if (!this.isUsingProxy) {
                        this.isUsingProxy = true;
                        this.video.src = this.getProxiedUrl(streamUrl);
                        this.video.play().catch(err => {
                            if (err.name !== 'AbortError') console.error('Proxy play failed:', err);
                        });
                    }
                });
            } else {
                // Priority 3: Try direct playback for non-HLS streams
                this.updateTranscodeStatus('direct', 'Direct Play');
                this.video.src = finalUrl;
                this.video.play().catch(e => {
                    if (e.name !== 'AbortError') console.log('Autoplay prevented:', e);
                });
            }

            this.onPlaybackStarted(channel);

        } catch (err) {
            console.error('Error playing channel:', err);
            this.showError('Failed to play channel');
        }
    }

    async updateTranscodeStatus(mode, text) {
        const el = document.getElementById('player-transcode-status');
        if (!el) return;

        el.className = 'transcode-status'; // Reset classes

        if (mode === 'hidden') {
            el.classList.add('hidden');
            return;
        }

        el.textContent = text || mode;
        el.classList.add(mode);

        // Ensure it's visible
        el.classList.remove('hidden');
    }

    /**
     * Get quality label from video height
     */
    getQualityLabel(height) {
        if (height >= 2160) return '4K';
        if (height >= 1440) return '1440p';
        if (height >= 1080) return '1080p';
        if (height >= 720) return '720p';
        if (height >= 480) return '480p';
        if (height > 0) return `${height}p`;
        return null;
    }

    /**
     * Update quality badge display
     */
    updateQualityBadge() {
        const badge = document.getElementById('player-quality-badge');
        if (!badge) return;

        if (this.currentStreamInfo?.height > 0) {
            badge.textContent = this.getQualityLabel(this.currentStreamInfo.height);
            badge.classList.remove('hidden');
        } else {
            badge.classList.add('hidden');
        }
    }

    /**
     * Fetch EPG data for current channel
     */
    async fetchEpgData(channel) {
        if (this.catchupInfo) return; // showing a past program, not live - see play()
        if (!channel || (!channel.tvgId && !channel.epg_id)) {
            this.updateNowPlaying(channel, null);
            return;
        }
        try {
            // First, try to use the centralized EpgGuide data (already loaded)
            if (window.app && window.app.epgGuide && window.app.epgGuide.programmes) {
                const epgGuide = window.app.epgGuide;

                // Get current program from EpgGuide
                const currentProgram = epgGuide.getCurrentProgram(channel.tvgId, channel.name);

                if (currentProgram) {
                    // Find upcoming programs from the guide's data
                    const epgChannel = epgGuide.channelMap?.get(channel.tvgId) ||
                        epgGuide.channelMap?.get(channel.name?.toLowerCase());

                    let upcoming = [];
                    if (epgChannel) {
                        const now = Date.now();
                        upcoming = epgGuide.programmes
                            .filter(p => p.channelId === epgChannel.id && new Date(p.start).getTime() > now)
                            .slice(0, 5)
                            .map(p => ({
                                title: p.title,
                                start: new Date(p.start),
                                stop: new Date(p.stop),
                                description: p.desc || ''
                            }));
                    }

                    this.updateNowPlaying(channel, {
                        current: {
                            title: currentProgram.title,
                            start: new Date(currentProgram.start),
                            stop: new Date(currentProgram.stop),
                            description: currentProgram.desc || ''
                        },
                        upcoming
                    });
                    return; // Success, exit early
                }
            }

            // Fallback: Try to get EPG from Xtream API if available
            if (channel.sourceType === 'xtream' && channel.streamId) {
                const epgData = await API.proxy.xtream.shortEpg(channel.sourceId, channel.streamId);
                if (epgData && epgData.epg_listings && epgData.epg_listings.length > 0) {
                    const listings = epgData.epg_listings;
                    const now = Math.floor(Date.now() / 1000);

                    // Find current program
                    const current = listings.find(p => {
                        const start = parseInt(p.start_timestamp);
                        const end = parseInt(p.stop_timestamp);
                        return start <= now && end > now;
                    });

                    // Get upcoming programs
                    const upcoming = listings
                        .filter(p => parseInt(p.start_timestamp) > now)
                        .slice(0, 5)
                        .map(p => ({
                            title: this.decodeBase64(p.title),
                            start: new Date(parseInt(p.start_timestamp) * 1000),
                            stop: new Date(parseInt(p.stop_timestamp) * 1000),
                            description: this.decodeBase64(p.description)
                        }));

                    if (current) {
                        this.updateNowPlaying(channel, {
                            current: {
                                title: this.decodeBase64(current.title),
                                start: new Date(parseInt(current.start_timestamp) * 1000),
                                stop: new Date(parseInt(current.stop_timestamp) * 1000),
                                description: this.decodeBase64(current.description)
                            },
                            upcoming
                        });
                    }
                }
            }
        } catch (err) {
            console.log('EPG data not available:', err.message);
        }
    }

    /**
     * Get proxied URL for a stream
     */
    getProxiedUrl(url) {
        return `/api/proxy/stream?url=${encodeURIComponent(url)}`;
    }

    /**
     * Get transcoded URL for a stream (audio transcoding for browser compatibility)
     */
    getTranscodeUrl(url) {
        return `/api/transcode?url=${encodeURIComponent(url)}`;
    }

    /**
     * Get remuxed URL for a stream (container conversion only, no re-encoding)
     * Used for raw .ts streams that browsers can't play directly
     */
    getRemuxUrl(url) {
        return `/api/remux?url=${encodeURIComponent(url)}`;
    }

    /**
     * Decode base64 EPG data
     */
    decodeBase64(str) {
        if (!str) return '';
        try {
            return decodeURIComponent(escape(atob(str)));
        } catch {
            return str;
        }
    }

    /**
     * Stop playback
     */
    async stop() {
        this.catchupInfo = null;

        // Stop any running transcode session first. Becoming async doesn't
        // change behavior for a caller that doesn't await it - only play()'s
        // own explicit `await this.stop()` (switching channels) actually
        // waits for the old connection to close now.
        await this.stopTranscodeSession();

        if (this.hls) {
            this.hls.destroy();
            this.hls = null;
        }
        this.video.pause();
        // See the matching fix/comment in WatchPage.js's stop() - pause()
        // alone doesn't abort a Direct Play native <video src> request, so
        // switching channels shortly after could leave the old connection
        // dangling and race the new one for this provider's single
        // connection slot. Clearing src + load() forces an immediate abort.
        this.video.removeAttribute('src');
        this.video.load();

        // Reset UI to idle state - also clear any leftover error styling/
        // content from showError() so a bare stop() (not followed by a new
        // play()) doesn't leave stale error text behind under the "select a
        // channel" state.
        this.overlay.classList.remove('hidden'); // Show "Select a channel"
        this.overlay.classList.remove('has-error');
        const overlayContent = this.overlay.querySelector('.overlay-content');
        if (overlayContent) overlayContent.innerHTML = '<p>Select a channel to start watching</p>';
        this.controlsOverlay?.classList.add('hidden'); // Hide controls
        this.loadingSpinner?.classList.remove('show');
        this.nowPlaying.classList.add('hidden');

        // Hide quality badge
        this.currentStreamInfo = null;
        const badge = document.getElementById('player-quality-badge');
        if (badge) badge.classList.add('hidden');
    }

    /**
     * Update now playing display
     */
    updateNowPlaying(channel, epgData = null) {
        const channelName = this.nowPlaying.querySelector('.channel-name');
        const programTitle = this.nowPlaying.querySelector('.program-title');
        const programTime = this.nowPlaying.querySelector('.program-time');
        const upNextList = document.getElementById('up-next-list');

        channelName.textContent = channel.name || channel.tvgName || 'Unknown Channel';

        // While playing back a past program via catch-up, always show that
        // program's info regardless of what the caller passed - the live
        // "current program" the guide reports has nothing to do with what's
        // actually on screen. See play()/fetchEpgData().
        const effectiveEpgData = this.catchupInfo ? { current: this.catchupInfo } : epgData;

        if (effectiveEpgData && effectiveEpgData.current) {
            programTitle.textContent = (this.catchupInfo ? '⏪ Catch-up: ' : '') + effectiveEpgData.current.title;
            const start = new Date(effectiveEpgData.current.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const end = new Date(effectiveEpgData.current.stop).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            programTime.textContent = `${start} - ${end}`;
        } else {
            programTitle.textContent = '';
            programTime.textContent = '';
        }

        // Update up next
        upNextList.innerHTML = '';
        if (epgData && epgData.upcoming) {
            epgData.upcoming.slice(0, 3).forEach(prog => {
                const li = document.createElement('li');
                const time = new Date(prog.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                li.textContent = `${time} - ${prog.title}`;
                upNextList.appendChild(li);
            });
        }
    }

    /**
     * Show error overlay. Marked with .has-error (in addition to removing
     * .hidden) so it stays visible even in the small TV Guide preview,
     * where the default "Select a channel" placeholder is normally hidden
     * as clutter - an actual playback failure is exactly the kind of
     * feedback that preview needs to show, not hide.
     */
    showError(message) {
        API.telemetry.report('playback_error', {
            channelId: this.currentChannel?.id,
            name: this.currentChannel?.name || this.currentChannel?.tvgName,
            message
        });
        this.overlay.classList.remove('hidden');
        this.overlay.classList.add('has-error');
        this.overlay.querySelector('.overlay-content').innerHTML = `<p style="color: var(--color-error);">${escapeHtml(message)}</p>`;
    }

    /**
     * Handle keyboard shortcuts
     */
    handleKeyboard(e) {
        if (document.activeElement.tagName === 'INPUT') return;

        switch (e.key) {
            case ' ':
            case 'k':
                e.preventDefault();
                this.video.paused ? this.video.play() : this.video.pause();
                break;
            case 'f':
                e.preventDefault();
                this.toggleFullscreen();
                break;
            case 'm':
                e.preventDefault();
                this.video.muted = !this.video.muted;
                break;
            case 'ArrowUp':
                if (!this.settings.arrowKeysChangeChannel) {
                    e.preventDefault();
                    this.video.volume = Math.min(1, this.video.volume + 0.1);
                }
                // If arrowKeysChangeChannel is true, let HomePage handle it
                break;
            case 'ArrowDown':
                if (!this.settings.arrowKeysChangeChannel) {
                    e.preventDefault();
                    this.video.volume = Math.max(0, this.video.volume - 0.1);
                }
                // If arrowKeysChangeChannel is true, let HomePage handle it
                break;
            case 'ArrowLeft':
                e.preventDefault();
                // Volume down when arrow keys are for channels
                if (this.settings.arrowKeysChangeChannel) {
                    this.video.volume = Math.max(0, this.video.volume - 0.1);
                }
                break;
            case 'ArrowRight':
                e.preventDefault();
                // Volume up when arrow keys are for channels
                if (this.settings.arrowKeysChangeChannel) {
                    this.video.volume = Math.min(1, this.video.volume + 0.1);
                }
                break;
            case 'PageUp':
            case 'ChannelUp':
                e.preventDefault();
                this.channelUp();
                break;
            case 'PageDown':
            case 'ChannelDown':
                e.preventDefault();
                this.channelDown();
                break;
            case 'i':
                // Show/hide info overlay
                e.preventDefault();
                if (this.nowPlaying.classList.contains('hidden')) {
                    this.showNowPlayingOverlay();
                } else {
                    this.hideNowPlayingOverlay();
                }
                break;
        }
    }

    /**
     * Go to previous channel
     */
    channelUp() {
        if (!window.app?.channelList) return;
        const channels = window.app.channelList.getVisibleChannels();
        if (channels.length === 0) return;

        const currentIdx = this.currentChannel
            ? channels.findIndex(c => c.id === this.currentChannel.id)
            : -1;

        const prevIdx = currentIdx <= 0 ? channels.length - 1 : currentIdx - 1;
        window.app.channelList.selectChannel({ channelId: channels[prevIdx].id });
    }

    /**
     * Go to next channel
     */
    channelDown() {
        if (!window.app?.channelList) return;
        const channels = window.app.channelList.getVisibleChannels();
        if (channels.length === 0) return;

        const currentIdx = this.currentChannel
            ? channels.findIndex(c => c.id === this.currentChannel.id)
            : -1;

        const nextIdx = currentIdx >= channels.length - 1 ? 0 : currentIdx + 1;
        window.app.channelList.selectChannel({ channelId: channels[nextIdx].id });
    }

    /**
     * Toggle fullscreen mode (cross-browser including Safari/iOS)
     */
    toggleFullscreen() {
        const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;

        if (isFullscreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        } else if (this.container) {
            if (this.container.requestFullscreen) {
                this.container.requestFullscreen().catch(err => console.error('Fullscreen error:', err));
            } else if (this.container.webkitRequestFullscreen) {
                this.container.webkitRequestFullscreen();
            } else if (this.video.webkitEnterFullscreen) {
                // iOS Safari: use native video fullscreen
                this.video.webkitEnterFullscreen();
            }
        }
    }

    /**
     * Enter fullscreen unconditionally (unlike toggleFullscreen(), never
     * exits) - used for double-click-to-fullscreen on a TV Guide channel
     * row, where double-clicking a *different* channel while already
     * fullscreen should just switch channel and stay fullscreen rather
     * than kicking the user back out.
     */
    enterFullscreen() {
        if (!document.fullscreenElement && this.container) {
            this.container.requestFullscreen();
        }
    }
}

// Export
window.VideoPlayer = VideoPlayer;
